import * as React from 'react'
import * as Lodash from 'lodash/fp'

import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  styled,
  Switch,
} from '@design/pt-ui'
import Loader from './Loader'
import CreativeCard from '../components/CreativeCard/CreativeCard'

import {
  Creative,
  creativeActionFromDescriptive,
} from '../interfaces/entities/creative'
import { Descriptive } from '../interfaces/base'

import { ICreativeService } from '../interfaces/services/creatives'
import { useDirtySanchez } from '../components/DirtySanchez'

import DI from '../app/di'
import CallToAction from './CallToAction'
import { translateWithUserLocale } from '../utils/i18n'
import { useDeepCompareEffect } from '../utils/hooks'
import { stripSpecialCharacters } from '../utils/string'

const CommentField = styled(TextField)(({ theme }) => ({
  border: '1px solid gray',
  borderRadius: theme.spacing(0.5),
  marginBottom: theme.spacing(3),
  '& .MuiInputBase-root': {
    padding: theme.spacing(0.5),
  },
}))

interface ICreativesTransitionProps {
  creatives: Creative[]
  action: Descriptive
  labels?: { confirm: string; cancel: string }
  onActionComplete: (response: {
    creatives: Creative[]
    warnings: string[]
  }) => any
  selectionFilter?: (creative: Creative) => boolean
  preselectedFilter?: (creative: Creative) => boolean
  creativeGroup?: (creative: Creative) => string
}

interface ICreativesTransitionState {
  selected: Creative[]
  loading: 'creatives'[]
  error: string
}

function CreativesTransition(props: ICreativesTransitionProps) {
  const getPreselected = (props: ICreativesTransitionProps) => {
    return props.creatives
      .map((c) => {
        if (props.preselectedFilter && props.preselectedFilter(c)) {
          if (props.selectionFilter && props.selectionFilter(c)) {
            return c
          } else {
            return null
          }
        } else {
          return null
        }
      })
      .filter((elem) => elem != null) as Creative[]
  }

  const [state, setState] = React.useState<ICreativesTransitionState>({
    selected: getPreselected(props),
    loading: [],
    error: '',
  })

  const { clean, defile } = useDirtySanchez()

  const textRef = React.useRef<HTMLInputElement>(null)

  const creativeIds = props.creatives.map((c) => c.id).sort()
  useDeepCompareEffect(() => {
    setState((state) => ({
      ...state,
      selected: getPreselected(props),
    }))
  }, [creativeIds])

  const creativeServiceFn = () => {
    return DI.get('CreativeService') as ICreativeService
  }

  const isSelectable = (creative: Creative) => {
    if (props.selectionFilter) {
      return props.selectionFilter(creative)
    }

    return true
  }

  const onClick = (creative: Creative) => {
    if (!isSelectable(creative)) {
      return
    }
    defile('creatives_transition')

    return setState((prevState) => {
      return {
        ...prevState,
        selected:
          prevState.selected.findIndex((s) => s.id === creative.id) !== -1
            ? prevState.selected.filter((s) => s.id !== creative.id)
            : [...prevState.selected, creative],
      }
    })
  }

  const doAction = async () => {
    if (state.selected.length > 0) {
      setState((state) => ({
        ...state,
        loading: [...state.loading, 'creatives'],
      }))

      const commentField = textRef.current
      const creativeService = creativeServiceFn()
      const creativesResponse = await creativeService.doCreativesAction(
        creativeActionFromDescriptive(props.action),
        state.selected,
        stripSpecialCharacters(commentField?.value || ''),
      )

      if (creativesResponse.type === 'error') {
        setState((state) => ({
          ...state,
          loading: state.loading.filter((l) => l !== 'creatives'),
          error: 'Could not update state.',
        }))
      } else {
        setState((state) => ({
          ...state,
          loading: state.loading.filter((l) => l !== 'creatives'),
          selected: [],
        }))

        clean('creatives_transition')
        props.onActionComplete(creativesResponse.payload)
      }
    }
  }

  const onSwitchChange = React.useCallback((value: string) => {
    if (value === 'on') {
      selectAll()
    }

    if (value === 'off') {
      unselectAll()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const selectAll = React.useCallback(() => {
    const allSelectable = props.creatives.filter((c) => isSelectable(c))
    setState((state) => ({
      ...state,
      selected: allSelectable,
    }))
  }, [props.creatives]) // eslint-disable-line react-hooks/exhaustive-deps

  const unselectAll = React.useCallback(() => {
    setState((state) => ({
      ...state,
      selected: [],
    }))
  }, [])

  const { creativeGroup = (creative: Creative) => '' } = props
  const groups = React.useMemo(
    () => Lodash.groupBy(creativeGroup, props.creatives),
    [creativeIds], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const maxSelectable = React.useMemo(
    () => props.creatives.map((c) => isSelectable(c)).filter((b) => b),
    [creativeIds], // eslint-disable-line react-hooks/exhaustive-deps
  ).length

  const selectedCount = state.selected.length

  return (
    <div>
      {state.loading.includes('creatives') ? (
        <Loader
          variant="overlay"
          message={translateWithUserLocale('loaders.sending_to_agency')}
        />
      ) : null}
      <Switch
        className="cp-transition-toggle-all"
        state={
          selectedCount === 0
            ? 'off'
            : selectedCount === maxSelectable
              ? 'on'
              : 'Schrödinger'
        }
        onChange={onSwitchChange}
        disabled={false}
        style={{ position: 'absolute', right: '10px', top: '12px' }}
        title={translateWithUserLocale(
          'creative_selection_modal.select_deselect_all',
        )}
      />
      {Object.keys(groups).map((groupName, index) => {
        return (
          <React.Fragment key={groupName}>
            <Typography variant="subtitle1" sx={{ margin: 1.5 }}>
              {groupName}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }} key={index}>
              {groups[groupName].map((creative) => {
                return (
                  <span onClick={() => onClick(creative)} key={creative.id}>
                    <CreativeCard
                      navigateOnClick={false}
                      state={
                        state.selected.findIndex(
                          (s) => s.id === creative.id,
                        ) !== -1
                          ? 'selected'
                          : isSelectable(creative)
                            ? null
                            : 'disabled'
                      }
                      creative={creative}
                      linkActive={false}
                    />
                  </span>
                )
              })}
            </Box>
            <Divider />
          </React.Fragment>
        )
      })}
      <Box sx={{ display: 'flex', flexFlow: 'column', marginTop: 4 }}>
        <Typography variant="subtitle1" sx={{ margin: 1.5 }}>
          {translateWithUserLocale('creative_selection_modal.comment')}
        </Typography>
        <CommentField
          fullWidth={true}
          multiline={true}
          rows={6}
          inputRef={textRef}
        />

        <CallToAction
          disabled={!state.selected.length}
          onClick={doAction}
          confirm={true}
          dialog={{
            title: translateWithUserLocale(
              'confirmation_dialog.are_you_sure_transition',
            ),
            message: translateWithUserLocale(
              'confirmation_dialog.are_you_sure_transition_message',
              {
                title: props.action.title,
              },
            ),
          }}>
          <Button
            className="cp-cta cp-cta-send-to-agency"
            variant="outlined"
            fullWidth={true}
            disabled={!state.selected.length}>
            {translateWithUserLocale('creative_selection_modal.send_to_agency')}
          </Button>
        </CallToAction>
      </Box>
    </div>
  )
}

export default CreativesTransition
